import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity,
  ArrowDownToLine,
  BarChart3,
  Check,
  Clock3,
  FileSpreadsheet,
  FileText,
  Search,
  Truck,
} from 'lucide-react';
import { CATEGORIES, loadState } from './data';
import {
  DemoState,
  DEMO_DAY,
  DEMO_NOW,
  STORAGE_KEY,
  businessDay,
  dateTime,
  metrics,
  operationalGroups,
  hasTripData,
} from './model';
import { managementTrips } from './management-data';
import Indicators, { Drill, OperationalSummary } from './indicators';
import { DrillDialog, Timeline, TripDetails, exportTrips } from './components';
import { EstadiaAccess, READ_ONLY_ACCESS } from './access';
import Settlement from './settlement';
import Reports from './reports';
import './workspace.css';
import type { EstadiaCommand, Justification } from './live-data';

const TABS = [
  { id: 'timeline', title: 'Acompanhamento', icon: Activity },
  { id: 'indicators', title: 'Gestão à vista', icon: BarChart3 },
  { id: 'settlement', title: 'Estadias', icon: FileText },
  { id: 'invoices', title: 'Faturas', icon: FileText },
  { id: 'reports', title: 'Relatórios', icon: FileSpreadsheet },
];
const PREVIOUS_VIEWS: Record<string, string> = {
  operation: 'settlement',
  billing: 'settlement',
  finance: 'settlement',
};

export type EstadiaWorkspaceProps = {
  showNavigation?: boolean;
  access?: EstadiaAccess;
  initialData?: DemoState;
  positionAt?: string;
  onCommand?: (command: EstadiaCommand) => Promise<void>;
  justifications?: { trip: Justification[]; billing: Justification[] };
  onRefresh?: () => void;
  busy?: boolean;
  onRangeChange?: (from: string, to: string) => void;
};

export default function EstadiaWorkspace({
  showNavigation = true,
  access = READ_ONLY_ACCESS,
  initialData,
  positionAt = DEMO_NOW,
  onCommand,
  justifications,
  onRefresh,
  busy,
  onRangeChange,
}: EstadiaWorkspaceProps) {
  const [state, setState] = useState<DemoState>(
    () => initialData || loadState()
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('view') || 'timeline';
  const requestedView = PREVIOUS_VIEWS[requested] || requested;
  const tabs = TABS.filter((tab) =>
    tab.id === 'timeline'
      ? true
      : tab.id === 'settlement'
        ? access.viewStays !== false
        : tab.id === 'invoices'
          ? access.viewBilling !== false
          : access.viewManagement !== false
  );
  const view = tabs.some((tab) => tab.id === requestedView)
    ? requestedView
    : tabs[0]?.id;
  const referenceDay = businessDay(positionAt);
  const [from, setFrom] = useState(`${referenceDay.slice(0, 7)}-01`);
  const [to, setTo] = useState(referenceDay);
  const [unit, setUnit] = useState('all');
  const [carrier, setCarrier] = useState('all');
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [drill, setDrill] = useState<Drill | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [storageError, setStorageError] = useState(false);
  useEffect(() => {
    if (initialData) setState(initialData);
  }, [initialData]);
  useEffect(() => {
    if (initialData) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      setStorageError(false);
    } catch {
      setStorageError(true);
    }
  }, [state, initialData]);
  const periodInvalid = view !== 'timeline' && (!from || !to || from > to);
  useEffect(() => {
    if (view === 'timeline' || periodInvalid || !onRangeChange) return;
    const timer = setTimeout(() => onRangeChange(from, to), 350);
    return () => clearTimeout(timer);
  }, [from, to, view, periodInvalid, onRangeChange]);
  const units = Array.from(
    new Set(
      state.trips
        .filter(
          (trip) =>
            view !== 'timeline' ||
            trip.circuit ||
            trip.sourceMissing?.includes('Circuito')
        )
        .map((trip) => trip.unit)
    )
  );
  const carriers = Array.from(new Set(state.trips.map((trip) => trip.carrier)));
  const applyDisplayRules = view === 'indicators' || view === 'timeline';
  const viewTrips = applyDisplayRules ? managementTrips(state.trips) : state.trips;
  const dimensionRows = viewTrips.filter(
    (trip) =>
      (unit === 'all' || trip.unit === unit) &&
      (carrier === 'all' || trip.carrier === carrier) &&
      (category === 'all' || trip.category === category) &&
      `${trip.id} ${trip.sourceId || ''} ${trip.plate}`
        .toLowerCase()
        .includes(query.toLowerCase())
  );
  const rows = periodInvalid
    ? []
    : dimensionRows.filter(
        (trip) =>
          businessDay(trip.events.scheduled) >= from &&
          businessDay(trip.events.scheduled) <= to
      );
  const timelineRows = periodInvalid
    ? []
    : dimensionRows.filter(
        (trip) =>
          (trip.circuit || trip.sourceMissing?.includes('Circuito'))
      );
  const { within: withinFree } = operationalGroups(timelineRows);
  const currentTrip = viewTrips.find((trip) => trip.id === selected);
  const pendingInvoices = state.invoices.filter(
    (invoice) =>
      invoice.status === 'pending' &&
      invoice.tripIds.some((id) => rows.some((trip) => trip.id === id))
  );
  function update(action: (current: DemoState) => DemoState, message: string) {
    try {
      setState(action(state));
      setNotice(message);
    } catch (error) {
      setNotice(
        error instanceof Error
          ? error.message
          : 'Não foi possível concluir a ação.'
      );
    }
  }
  function changeView(next: string) {
    if (
      next === 'timeline' &&
      !state.trips.some((trip) => trip.circuit && trip.unit === unit)
    )
      setUnit('all');
    setSearchParams({ view: next });
    setNotice('');
  }
  return (
    <div className="stay-workspace-app">
      <header className="stay-page-heading">
        <div>
          <span className="stay-eyebrow">OPERAÇÕES · CORTEVA</span>
          <h1>Controle de estadia</h1>
          <p>
            {initialData
              ? 'Acompanhamento de viagens e faturamento'
              : 'Franco da Rocha e Paulínia · Circuito UNO'}
          </p>
        </div>
        <div className="stay-header-actions">
          {onRefresh && (
            <button disabled={busy} onClick={onRefresh}>
              {busy ? 'Atualizando…' : 'Atualizar dados'}
            </button>
          )}
          <span>
            <Clock3 size={15} />{' '}
            {onRefresh ? 'Dados consultados' : 'Última posição'}:{' '}
            {dateTime(positionAt)}
          </span>
          <button
            onClick={() =>
              exportTrips(view === 'timeline' ? timelineRows : rows)
            }
          >
            <ArrowDownToLine size={16} /> Exportar viagens
          </button>
        </div>
      </header>
      {showNavigation && (
        <nav className="stay-workspace-tabs" aria-label="Controle de estadia">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={view === tab.id ? 'active' : ''}
              aria-current={view === tab.id ? 'page' : undefined}
              onClick={() => changeView(tab.id)}
            >
              <tab.icon size={18} />
              {tab.title}
              {tab.id === 'settlement' &&
                access.viewInvoices &&
                pendingInvoices.length > 0 && (
                  <small>{pendingInvoices.length}</small>
                )}
            </button>
          ))}
        </nav>
      )}
      <div className="stay-global-filters">
        {view !== 'timeline' && (
          <>
            <label className="stay-field stay-date-field">
              De
              <input
                aria-label="Data inicial"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </label>
            <label className="stay-field stay-date-field">
              Até
              <input
                aria-label="Data final"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </label>
          </>
        )}
        {state.trips.some((trip) => hasTripData(trip, 'Unidade')) && (
          <label className="stay-field">
            Unidade
            <select
              aria-label="Filtrar unidade"
              value={unit}
              onChange={(event) => setUnit(event.target.value)}
            >
              <option value="all">Todas as unidades</option>
              {units.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
        )}
        <label className="stay-field stay-carrier-field">
          Transportadora
          <select
            aria-label="Filtrar transportadora"
            value={carrier}
            onChange={(event) => setCarrier(event.target.value)}
          >
            <option value="all">Todas as transportadoras</option>
            {carriers.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        {state.trips.some((trip) =>
          hasTripData(trip, 'Categoria de produto')
        ) && (
          <label className="stay-field">
            Categoria
            <select
              aria-label="Filtrar categoria"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="all">Todas as categorias</option>
              {Array.from(
                new Set([
                  ...CATEGORIES,
                  ...state.trips.map((trip) => trip.category),
                ])
              ).map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </label>
        )}
        <label className="stay-field stay-query">
          Placa ou viagem
          <div>
            <Search size={16} />
            <input
              aria-label="Buscar placa ou viagem"
              placeholder="Placa ou ID"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        </label>
        <button
          className="stay-clear"
          onClick={() => {
            setFrom(
              view === 'timeline'
                ? referenceDay
                : `${referenceDay.slice(0, 7)}-01`
            );
            setTo(referenceDay);
            setUnit('all');
            setCarrier('all');
            setCategory('all');
            setQuery('');
          }}
        >
          Limpar filtros
        </button>
      </div>
      <div className="stay-context-line">
        <span>Franquia: 10 horas · Tarifa adicional: R$ 46,62/h</span>
        <span>
          {view === 'timeline'
            ? 'Viagens não finalizadas · horários de Brasília'
            : 'Período por data de agendamento'}
        </span>
      </div>
      {periodInvalid && (
        <div className="stay-callout error" role="alert">
          Informe um período válido. A data inicial não pode ser posterior à
          final.
        </div>
      )}
      {notice && (
        <div className="stay-notice" role="status">
          <Check size={16} />
          {notice}
          <button aria-label="Fechar mensagem" onClick={() => setNotice('')}>
            ×
          </button>
        </div>
      )}
      {storageError && (
        <div className="stay-callout error" role="alert">
          Não foi possível salvar as alterações. Mantenha esta página aberta
          para não perder os dados.
        </div>
      )}
      <div className="stay-view-content">
        {!view && (
          <p role="alert">
            Seu usuário não possui permissão para acessar estas visualizações.
          </p>
        )}
        {view === 'timeline' && (
          <>
            <OperationalSummary trips={timelineRows} onDrill={setDrill} />
            <Timeline
              trips={timelineRows}
              onSelect={(trip) => setSelected(trip.id)}
              day={to || DEMO_DAY}
              positionAt={positionAt}
            />
            <section className="stay-quick-bands">
              <div>
                <Truck size={19} />
                <h3>Tempo restante</h3>
                <p>Viagens em operação</p>
              </div>
              {[
                { label: 'Até 1h', min: 0, max: 60 },
                { label: '> 1h até 2h', min: 60, max: 120 },
                { label: '> 2h até 5h', min: 120, max: 300 },
                { label: 'Mais de 5h', min: 300, max: Infinity },
              ].map((band) => {
                const trips = withinFree.filter((trip) => {
                  const remaining = metrics(trip).remaining;
                  return (
                    (band.min === 0 ? remaining >= 0 : remaining > band.min) &&
                    remaining <= band.max
                  );
                });
                return (
                  <button
                    key={band.label}
                    onClick={() =>
                      setDrill({
                        title: `Tempo restante · ${band.label}`,
                        trips,
                        showRemaining: true,
                      })
                    }
                  >
                    <strong>{trips.length}</strong>
                    <span>{band.label}</span>
                  </button>
                );
              })}
            </section>
          </>
        )}
        {view === 'indicators' && (
          <Indicators
            state={state}
            rows={rows}
            dimensionRows={dimensionRows}
            referenceDate={referenceDay}
            onDrill={setDrill}
          />
        )}
        {(view === 'settlement' || view === 'invoices') && (
          <Settlement
            section={view === 'invoices' ? 'invoices' : 'stays'}
            referenceDate={referenceDay}
            state={state}
            rows={rows}
            access={access}
            update={update}
            onCommand={onCommand}
            justifications={justifications}
            busy={busy}
            onSelect={(trip) => setSelected(trip.id)}
          />
        )}
        {view === 'reports' && (
          <Reports
            state={state}
            rows={rows}
            onSelect={(trip) => setSelected(trip.id)}
          />
        )}
      </div>
      {drill && (
        <DrillDialog
          drill={drill}
          onSelect={(trip) => setSelected(trip.id)}
          onClose={() => setDrill(null)}
        />
      )}
      {currentTrip && (
        <TripDetails
          trip={currentTrip}
          hideSourceStatus={applyDisplayRules}
          state={state}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
